import { API_NEW_SAP_BARCODE } from 'utils/constants'
import Api from './axiosInstance'

// The only filter keys the API understands. Anything else it receives is turned
// into a case-insensitive regex filter on a field of that name, so a stray key
// (`sortBy`, `isEditor`, …) silently returns an empty listing. Whitelisting here
// keeps that impossible for all three read endpoints.
const FILTER_KEYS = [
  'search',
  'barcode',
  'description',
  'isSeen',
  'from',
  'to',
] as const

// `extra` carries non-filter params (page/limit) and bypasses the whitelist.
// Empty values are dropped so blanks never reach the server — note `isSeen`
// omitted means "seen and unseen"; `isSeen=false` means unseen only.
const buildQueryString = (
  filters: Record<string, any> = {},
  extra: Record<string, any> = {},
) => {
  const searchParams = new URLSearchParams()

  const append = (key: string, value: any) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.set(key, String(value))
    }
  }

  FILTER_KEYS.forEach((key) => append(key, filters[key]))
  Object.entries(extra).forEach(([key, value]) => append(key, value))

  return searchParams.toString()
}

const withQuery = (path: string, queryString: string) =>
  queryString ? `${path}?${queryString}` : path

// Paginated listing. Sorting is fixed server-side (createdAt descending), so
// there is no sort param to pass.
export const fetchNewSapBarcodes = (
  filters: Record<string, any>,
  pagination: { page: number | string; limit: number | string },
) => {
  return Api.get({
    url: withQuery(
      API_NEW_SAP_BARCODE.NEW_SAP_BARCODE,
      buildQueryString(filters, pagination),
    ),
  })
}

// Bulk "mark as read" — flips the given (unseen) entries to isSeen: true.
// Requires edit access to the page; a view-only user gets a 401.
export const markNewSapBarcodeSeen = (ids: string[]) => {
  return Api.patch({
    url: API_NEW_SAP_BARCODE.MARK_SEEN,
    data: { ids },
  })
}

// Unseen count only (no rows) for the sidebar badge — responds with
// `{ data: { unseenCount } }`. Called with no filters; `isSeen` is forced to
// false server-side either way.
export const fetchNewSapBarcodeUnseenCount = (
  filters: Record<string, any> = {},
) => {
  return Api.get({
    url: withQuery(API_NEW_SAP_BARCODE.UNSEEN_COUNT, buildQueryString(filters)),
  })
}

// Queues a CSV export of the filtered rows — asynchronous. A 200 only means the
// job was accepted: a worker mails the requesting user a download link and logs
// the run on the Export Logs screen under module NEW_SAP_BARCODE.
// Responds 422 with `errors.msg.runningService` when an export is already
// in flight (one at a time, across all users).
export const exportNewSapBarcodes = (filters: Record<string, any> = {}) => {
  return Api.get({
    url: withQuery(API_NEW_SAP_BARCODE.EXPORT, buildQueryString(filters)),
  })
}
